import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useToast } from './Toast';
import Ps2GameBadge from './Ps2GameBadge';
import { CopyIcon } from './Icons';
import { usePS2Graphics } from '../context/PS2GraphicsContext';

const GAME_TITLE_MAP = {
  'socom-ii': 'SOCOM II: U.S. Navy SEALs',
  'socom-3': 'SOCOM 3: U.S. Navy SEALs',
  'socom-ca': 'SOCOM Combined Assault',
  'socom-1': 'SOCOM: U.S. Navy SEALs',
  'atv-offroad-fury-2': 'ATV Offroad Fury 2',
  'battlefront-2': 'Star Wars Battlefront II',
  'killzone': 'Killzone',
  'monster-hunter': 'Monster Hunter',
  '25-to-life': '25 to Life',
  'burnout-3': 'Burnout 3: Takedown',
};

function resolveGameTitle(lobby) {
  if (lobby.gameTitle) return lobby.gameTitle;
  if (lobby.gameSlug && GAME_TITLE_MAP[lobby.gameSlug.toLowerCase()]) {
    return GAME_TITLE_MAP[lobby.gameSlug.toLowerCase()];
  }
  if (lobby.gameName && !lobby.gameName.toLowerCase().includes('guidance') && !lobby.gameName.toLowerCase().includes('8x8') && !lobby.gameName.toLowerCase().includes('room')) {
    return lobby.gameName;
  }
  if (lobby.gameSlug) {
    return lobby.gameSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  return 'SOCOM II';
}

function formatKdRatio(kills, deaths, explicitRatio) {
  if (explicitRatio !== undefined && explicitRatio !== null) return explicitRatio;
  if (kills === undefined || kills === null) return '0.00';
  if (!deaths || deaths === 0) return kills.toFixed(2);
  return (kills / deaths).toFixed(2);
}

/**
 * Helper to parse team scores and group players into 2 teams
 */
function parseTeams(lobby) {
  const stats = lobby.playerStats || [];
  const players = lobby.players || [];
  const teamScores = lobby.teamScores || {};

  const sealTeamStats = stats.filter(s => s.team && s.team.toUpperCase().includes('SEAL'));
  const terrTeamStats = stats.filter(s => s.team && (s.team.toUpperCase().includes('TERRORIST') || s.team.toUpperCase().includes('TERR')));
  
  const redTeamStats = stats.filter(s => s.team && s.team.toUpperCase() === 'RED');
  const blueTeamStats = stats.filter(s => s.team && s.team.toUpperCase() === 'BLUE');

  let team1Name = 'SEALS';
  let team2Name = 'TERRORISTS';
  let team1Score = 0;
  let team2Score = 0;

  const scoreKeys = Object.keys(teamScores);
  if (scoreKeys.length >= 2) {
    team1Name = scoreKeys[0].toUpperCase();
    team1Score = teamScores[scoreKeys[0]];
    team2Name = scoreKeys[1].toUpperCase();
    team2Score = teamScores[scoreKeys[1]];
  } else if (lobby.score && typeof lobby.score === 'string') {
    const match = lobby.score.match(/([A-Z]+)?\s*(\d+)\s*-\s*(\d+)\s*([A-Z]+)?/i);
    if (match) {
      if (match[1]) team1Name = match[1].toUpperCase();
      team1Score = parseInt(match[2]);
      team2Score = parseInt(match[3]);
      if (match[4]) team2Name = match[4].toUpperCase();
    }
  }

  let team1Players = [];
  let team2Players = [];

  if (sealTeamStats.length > 0 || terrTeamStats.length > 0) {
    team1Name = 'SEALS';
    team2Name = 'TERRORISTS';
    team1Players = sealTeamStats;
    team2Players = terrTeamStats;
  } else if (redTeamStats.length > 0 || blueTeamStats.length > 0) {
    team1Name = 'RED';
    team2Name = 'BLUE';
    team1Players = redTeamStats;
    team2Players = blueTeamStats;
  } else if (stats.length > 0) {
    const mid = Math.ceil(stats.length / 2);
    team1Players = stats.slice(0, mid);
    team2Players = stats.slice(mid);
  } else if (players.length > 0) {
    const mid = Math.ceil(players.length / 2);
    team1Players = players.slice(0, mid).map(name => ({ name }));
    team2Players = players.slice(mid).map(name => ({ name }));
  }

  const isTeamMatch = team1Players.length > 0 || team2Players.length > 0 || scoreKeys.length >= 2;

  return {
    isTeamMatch,
    team1: { name: team1Name, score: team1Score, players: team1Players },
    team2: { name: team2Name, score: team2Score, players: team2Players },
  };
}

/**
 * Active Games section — compact, highly informative live match cards showing game title,
 * lobby info, map rotation playlist dropdown, 2-column team rosters with K/D and K/D Ratio, and copyable DNS button.
 */
export default function ActiveGames() {
  const [activeGames, setActiveGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const { ps2Mode } = usePS2Graphics();
  const toast = useToast();

  useEffect(() => {
    const socket = io();

    socket.on('connect', () => {
      console.log('🔌 Connected to TheCountryClub server');
    });

    socket.on('games_updated', (data) => {
      const gamesArray = Array.isArray(data) ? data : [];
      setActiveGames(gamesArray);
      setLoading(false);
    });

    socket.on('disconnect', () => {
      console.log('🔌 Disconnected from server');
    });

    return () => socket.disconnect();
  }, []);

  const copyDns = useCallback((dns, e) => {
    if (e) e.stopPropagation();
    if (!dns) return;
    navigator.clipboard.writeText(dns);
    toast.success(`📋 Copied DNS: ${dns}`);
  }, [toast]);

  const totalPlayers = useMemo(() => {
    return activeGames.reduce((sum, g) => {
      if (typeof g.playerCount === 'number') return sum + g.playerCount;
      const count = parseInt(g.current?.split('/')[0]) || g.players?.length || 0;
      return sum + count;
    }, 0);
  }, [activeGames]);

  return (
    <section id="active-games" className="relative z-30 bg-gradient-to-b from-transparent via-black/40 to-black/60 backdrop-blur-sm px-4 md:px-6 py-6 md:py-10">
      {/* Soft gradient divider at top */}
      <div className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent ${ps2Mode ? 'via-cyan-500/25' : 'via-emerald-500/25'} to-transparent`} />

      <div className="max-w-4xl mx-auto w-full space-y-4">
        {/* Section header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${ps2Mode ? 'bg-cyan-400' : 'bg-emerald-400'} opacity-75`} />
              <span className={`relative inline-flex rounded-full h-3 w-3 ${ps2Mode ? 'bg-cyan-500' : 'bg-emerald-500'}`} />
            </span>
            <h2 className="text-lg md:text-xl font-bold text-white tracking-tight flex items-center gap-2">
              Members at the Bar
              <span className={`text-xs px-2.5 py-0.5 rounded-full font-mono font-bold ${ps2Mode ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'} tabular-nums`}>
                {loading ? '...' : `${totalPlayers} Online`}
              </span>
            </h2>
          </div>
        </div>

        {/* Content Display */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="skeleton h-48 rounded-xl" style={{ animationDelay: `${i * 0.15}s` }} />
            ))}
          </div>
        ) : activeGames.length > 0 ? (
          <div className="space-y-6">
            {activeGames.map((game, idx) =>
              game.isPSRewired ? (
                <PSRewiredCard
                  key={game.id || `psr-${idx}`}
                  lobby={game}
                  ps2Mode={ps2Mode}
                />
              ) : (
                <MatchCard
                  key={game.id || idx}
                  lobby={game}
                  copyDns={copyDns}
                  ps2Mode={ps2Mode}
                />
              )
            )}
          </div>
        ) : (
          <div className="py-8 text-center rounded-xl bg-zinc-950/60 border border-white/[0.06] p-6 space-y-2">
            <p className="text-white/60 text-sm font-medium">No active lobbies reported right now</p>
            <p className="text-white/30 text-xs mt-1 font-mono leading-relaxed">
              This doesn't mean nobody is playing — game server hosts are responsible<br className="hidden sm:inline" />
              for posting live data. Webhooks are available for providers looking to integrate.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Compact MatchCard component explicitly displaying Game Title, Map, Map Playlist Dropdown,
 * Room Name, 2-column Team Rosters with K/D and K/D Ratio columns, and Clickable DNS Pill.
 */
function MatchCard({ lobby, copyDns, ps2Mode }) {
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const dns = lobby.dnsAddress || lobby.dns || '74.91.113.233';
  const mapName = lobby.map || 'Guidance';
  const gameTitle = resolveGameTitle(lobby);
  const roomName = lobby.gameName || 'Guidance 8x8';
  const channelName = lobby.channel || 'Keko\'s Server';
  const playerCount = lobby.current || `${lobby.players?.length || 0}/16`;
  const isInGame = lobby.inGame !== false;
  
  const playlist = lobby.playlist || lobby.mapRotation || lobby.maps || ['Guidance', 'Crossroads', 'Fish Hook', 'Desert Glory'];
  const currentIndex = lobby.currentLevelIndex !== undefined
    ? lobby.currentLevelIndex
    : playlist.findIndex(m => m.toLowerCase() === mapName.toLowerCase());

  const teamsData = useMemo(() => parseTeams(lobby), [lobby]);

  return (
    <div className="space-y-1.5">
      {/* Game Title & Room Name Above Card */}
      <div className="flex items-center justify-between px-1 text-xs font-mono">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-extrabold text-emerald-400 tracking-wide">{gameTitle}</span>
          <span className="text-white/30">•</span>
          <span className="text-white/70 truncate">{roomName} <span className="text-white/40">({channelName})</span></span>
        </div>
      </div>

      {/* Main Compact Card Container */}
      <div className="rounded-xl bg-zinc-950/95 border border-white/[0.08] hover:border-emerald-500/30 shadow-[0_6px_24px_rgba(0,0,0,0.85)] transition-all duration-300 overflow-hidden">
        {/* Top Header Bar */}
        <div className="bg-white/[0.02] border-b border-white/[0.06] px-3.5 py-2.5 flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex items-center gap-2.5">
            <Ps2GameBadge gameId={lobby.gameSlug} gameName={gameTitle} />
            <div className="flex items-center gap-2 font-mono text-xs flex-wrap">
              <span className="px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 font-bold">
                {gameTitle}
              </span>
              <span className="text-white/30">•</span>
              <span className="flex items-center gap-1.5 text-white/90 font-bold">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                {mapName}
              </span>
              <span className="text-white/30">•</span>
              <span className="text-white/50 hidden sm:inline">{channelName}</span>

              {/* Map Playlist Dropdown Pill */}
              {playlist && playlist.length > 0 && (
                <div className="relative inline-block ml-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); setPlaylistOpen(!playlistOpen); }}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/[0.05] border border-white/[0.1] hover:border-emerald-500/40 hover:bg-white/[0.1] transition-all text-[10px] font-mono text-white/80 cursor-pointer"
                    title="View map rotation playlist"
                  >
                    <span>🗺️ Rotation ({playlist.length})</span>
                    <svg className={`w-3 h-3 text-white/40 transition-transform ${playlistOpen ? 'rotate-180 text-emerald-400' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {playlistOpen && (
                    <div className="absolute left-0 sm:right-0 sm:left-auto mt-1.5 w-52 rounded-xl bg-zinc-950 border border-white/15 shadow-2xl p-2.5 z-50 font-mono text-xs space-y-1">
                      <div className="text-[10px] text-white/40 uppercase tracking-wider px-1.5 py-1 border-b border-white/10 font-bold flex items-center justify-between">
                        <span>Map Rotation</span>
                        <span className="text-emerald-400 font-extrabold">{playlist.length} Maps</span>
                      </div>
                      <div className="max-h-48 overflow-y-auto space-y-1 pt-1">
                        {playlist.map((mapItem, idx) => {
                          const isActive = idx === currentIndex || mapItem.toLowerCase() === mapName.toLowerCase();
                          return (
                            <div
                              key={idx}
                              className={`flex items-center justify-between px-2 py-1.5 rounded-md text-[11px] transition-colors ${
                                isActive
                                  ? 'bg-emerald-500/20 text-emerald-300 font-bold border border-emerald-500/40'
                                  : 'text-white/70 hover:bg-white/5'
                              }`}
                            >
                              <span className="truncate flex items-center gap-1.5">
                                <span className="text-white/30 text-[10px]">{idx + 1}.</span>
                                {mapItem}
                              </span>
                              {isActive && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-200 uppercase font-extrabold tracking-wider">
                                  PLAYING
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Copyable DNS Pill */}
            <button
              onClick={(e) => copyDns(dns, e)}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500/[0.08] border border-emerald-500/25 hover:border-emerald-500/50 hover:bg-emerald-500/20 transition-all text-xs font-mono text-emerald-300 cursor-pointer group/dns"
              title="Click to copy DNS Address"
            >
              <span className="text-[10px] uppercase font-bold text-emerald-500/70">DNS</span>
              <span className="font-bold">{dns}</span>
              <CopyIcon className="w-3 h-3 text-emerald-400/60 group-hover/dns:text-emerald-300" />
            </button>

            <span className="text-xs font-mono font-bold text-white/80 bg-white/[0.05] px-2.5 py-1 rounded-lg border border-white/[0.08]">
              {playerCount}
            </span>

            <span className={`text-[10px] font-mono font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-md border ${
              isInGame
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400'
                : 'bg-amber-500/15 border-amber-500/40 text-amber-400'
            }`}>
              {isInGame ? 'LIVE' : 'LOBBY'}
            </span>
          </div>
        </div>

        {/* Card Body - Compact 2-Column Split Roster with K/D and K/D Ratio */}
        <div className="p-3.5 md:p-4">
          {teamsData.isTeamMatch ? (
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/[0.08]">
              {/* Left Team (SEALS) */}
              <div className="pb-3 md:pb-0 md:pr-4 space-y-2">
                <div className="flex items-center justify-between border-b border-blue-500/25 pb-1.5">
                  <span className="text-xs font-extrabold font-mono tracking-wider uppercase text-blue-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-400 shadow-[0_0_8px_rgba(96,165,250,0.6)]" />
                    {teamsData.team1.name}
                  </span>
                  <span className="text-xl md:text-2xl font-mono font-black text-blue-400">
                    {teamsData.team1.score}
                  </span>
                </div>

                {/* Roster Header */}
                <div className="flex items-center justify-between px-2 text-[10px] font-mono text-white/30 uppercase tracking-wider">
                  <span>Player</span>
                  <div className="flex items-center gap-4">
                    <span>K / D</span>
                    <span className="w-10 text-right">Ratio</span>
                  </div>
                </div>

                {/* Player List */}
                <div className="space-y-1">
                  {teamsData.team1.players.map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between px-2 py-1 rounded bg-white/[0.02] hover:bg-white/[0.05] transition-colors text-xs font-mono">
                      <span className="text-white/90 font-medium truncate max-w-[130px]">
                        {p.name}
                      </span>
                      <div className="flex items-center gap-4 text-white/50 text-[11px] tabular-nums">
                        <span className="text-white/70">
                          {p.kills !== undefined ? `${p.kills}/${p.deaths}` : '-'}
                        </span>
                        <span className="w-10 text-right font-bold text-emerald-400/90">
                          {formatKdRatio(p.kills, p.deaths, p.kdRatio)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Team (TERRORISTS) */}
              <div className="pt-3 md:pt-0 md:pl-4 space-y-2">
                <div className="flex items-center justify-between border-b border-rose-500/25 pb-1.5">
                  <span className="text-xs font-extrabold font-mono tracking-wider uppercase text-rose-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-rose-400 shadow-[0_0_8px_rgba(251,113,133,0.6)]" />
                    {teamsData.team2.name}
                  </span>
                  <span className="text-xl md:text-2xl font-mono font-black text-rose-400">
                    {teamsData.team2.score}
                  </span>
                </div>

                {/* Roster Header */}
                <div className="flex items-center justify-between px-2 text-[10px] font-mono text-white/30 uppercase tracking-wider">
                  <span>Player</span>
                  <div className="flex items-center gap-4">
                    <span>K / D</span>
                    <span className="w-10 text-right">Ratio</span>
                  </div>
                </div>

                {/* Player List */}
                <div className="space-y-1">
                  {teamsData.team2.players.map((p, idx) => (
                    <div key={idx} className="flex items-center justify-between px-2 py-1 rounded bg-white/[0.02] hover:bg-white/[0.05] transition-colors text-xs font-mono">
                      <span className="text-white/90 font-medium truncate max-w-[130px]">
                        {p.name}
                      </span>
                      <div className="flex items-center gap-4 text-white/50 text-[11px] tabular-nums">
                        <span className="text-white/70">
                          {p.kills !== undefined ? `${p.kills}/${p.deaths}` : '-'}
                        </span>
                        <span className="w-10 text-right font-bold text-emerald-400/90">
                          {formatKdRatio(p.kills, p.deaths, p.kdRatio)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Free For All View */
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {lobby.players?.map((p, idx) => (
                <div key={idx} className="px-3 py-1 rounded-lg bg-white/[0.02] border border-white/[0.04] text-xs font-mono text-white/80 truncate">
                  {typeof p === 'string' ? p : p.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * PSRewiredCard — Consolidated SOCOM 1 live lobby card.
 * Shows room breakdowns, active game locations (maps being played),
 * and a collapsible player list pulled from PSRewired API.
 */
function PSRewiredCard({ lobby, ps2Mode }) {
  const [playersExpanded, setPlayersExpanded] = useState(false);
  if (!lobby) return null;

  const accentColor = ps2Mode ? 'cyan' : 'emerald';
  const totalPlayers = lobby.players?.length || 0;
  const gameTitle = lobby.gameTitle || 'SOCOM: U.S. Navy SEALs';

  return (
    <div className="space-y-1.5">
      {/* Title above card */}
      <div className="flex items-center justify-between px-1 text-xs font-mono">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`font-extrabold text-sky-400 tracking-wide`}>PSRewired</span>
          <span className="text-white/30">•</span>
          <span className="text-white/70 truncate">{gameTitle}</span>
        </div>
      </div>

      {/* Main Card */}
      <div className={`rounded-xl bg-zinc-950/95 border hover:border-sky-500/30 shadow-[0_6px_24px_rgba(0,0,0,0.85)] transition-all duration-300 overflow-hidden ${
        ps2Mode ? 'border-cyan-500/15' : 'border-white/[0.08]'
      }`}>
        {/* Header Bar */}
        <div className="bg-white/[0.02] border-b border-white/[0.06] px-3.5 py-2.5 flex flex-wrap items-center justify-between gap-2.5">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center gap-2 font-mono text-xs flex-wrap">
              <span className="px-2 py-0.5 rounded bg-sky-500/15 border border-sky-500/30 text-sky-300 font-bold">
                {gameTitle}
              </span>
              <span className="text-white/30">•</span>
              <span className="flex items-center gap-1.5 text-white/70 font-medium">
                <span className="text-sky-400 animate-pulse">●</span>
                PSRewired Network
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Player count pill */}
            <span className="text-xs font-mono font-bold text-white/80 bg-white/[0.05] px-2.5 py-1 rounded-lg border border-white/[0.08]">
              {totalPlayers} Online
            </span>

            {/* Status badge */}
            <span className={`text-[10px] font-mono font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-md border ${
              lobby.inGame
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400'
                : 'bg-amber-500/15 border-amber-500/40 text-amber-400'
            }`}>
              {lobby.inGame ? 'IN GAME' : 'IN LOBBY'}
            </span>
          </div>
        </div>

        {/* Card Body */}
        <div className="p-3.5 md:p-4 space-y-4">
          {/* Room breakdowns */}
          {lobby.rooms && lobby.rooms.length > 0 && (
            <div>
              <p className="text-white/40 font-mono text-[10px] uppercase tracking-wider mb-2 font-bold">Lobby Rooms</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                {lobby.rooms.map((room, rIdx) => {
                  const hasPlayers = room.count > 0;
                  return (
                    <div
                      key={rIdx}
                      className={`rounded-lg p-3 transition-all duration-300 ${
                        hasPlayers
                          ? 'bg-sky-500/10 border border-sky-500/30 text-sky-200 shadow-inner'
                          : 'bg-white/[0.02] border border-white/[0.06] text-white/40'
                      }`}
                    >
                      <p className={`font-mono text-[10px] uppercase tracking-wider mb-0.5 ${hasPlayers ? 'text-sky-400 font-semibold' : 'text-white/30'}`}>
                        {room.name}
                      </p>
                      <p className={`text-xl font-bold ${hasPlayers ? 'text-white' : 'text-white/40'}`}>
                        {room.count} <span className="text-xs font-normal opacity-40">/ 256</span>
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Active game locations (in-game matches) */}
          {lobby.locations && lobby.locations.length > 0 && (
            <div className="border-t border-white/[0.06] pt-3">
              <p className="text-white/40 font-mono text-[10px] uppercase tracking-wider mb-2 font-bold">Active Game Rooms</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {lobby.locations.map((loc, lIdx) => (
                  <div key={lIdx} className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-emerald-300 text-sm">{loc.name}</p>
                      <p className="text-xs text-white/50 font-mono mt-0.5">Map: {loc.map}</p>
                    </div>
                    <span className="px-2 py-1 bg-emerald-500/20 rounded text-xs font-bold text-emerald-300 border border-emerald-500/30">
                      {loc.players} / {loc.maxPlayers}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Collapsible player list */}
          {lobby.players && lobby.players.length > 0 && (
            <div className="border-t border-white/[0.06] pt-3">
              <button
                onClick={() => setPlayersExpanded(!playersExpanded)}
                className="flex items-center justify-between w-full text-left group"
              >
                <p className="text-white/40 font-mono text-[10px] uppercase tracking-wider font-bold group-hover:text-white/60 transition-colors">
                  Online Players ({lobby.players.length})
                </p>
                <svg
                  className={`w-3.5 h-3.5 text-white/30 group-hover:text-white/50 transition-all ${playersExpanded ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {playersExpanded && (
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                  {lobby.players.map((player, pIdx) => (
                    <div
                      key={pIdx}
                      className="px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.05] text-xs font-mono text-white/75 truncate hover:bg-white/[0.06] transition-colors"
                    >
                      {typeof player === 'string' ? player : player.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* PSRewired attribution link */}
        <div className="px-3.5 py-2 bg-white/[0.01] border-t border-white/[0.04] flex items-center justify-between">
          <span className="text-[10px] text-white/25 font-mono">Live data from PSRewired</span>
          <a
            href="https://psrewired.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sky-400/50 hover:text-sky-400 transition-all duration-200 hover:scale-110 flex items-center gap-1.5"
            title="Visit PSRewired"
          >

            <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 24 24" className="w-4 h-4" xmlns="http://www.w3.org/2000/svg">
              <path fill="none" d="M0 0h24v24H0z" />
              <path d="M15 7.5V2H9v5.5l3 3 3-3zM7.5 9H2v6h5.5l3-3-3-3zM9 16.5V22h6v-5.5l-3-3-3 3zM16.5 9l-3 3 3 3H22V9h-5.5z" />
            </svg>
          </a>
        </div>
      </div>
    </div>
  );
}
